import { CollectionResponse, ContentResponse } from "@authory-packages/core";
import { VerticalSpacing } from "../../VerticalSpacing";
import { V3IconChevronDown, V3IconSort } from "../../Icons";
import { UnstyledInlineLink } from "../../ReusableStyledComponents/UnstyledLink";
import { V3CaptionGrey35 } from "../../ReusableStyledComponents/V3CaptionGrey35";
import { V3ItemPreview } from "../../V3ItemPreview";
import { AddContentPreviewResultWrapper, AddItemPreviewResToggle, ContentResultIconWrapper } from "./AddContent.styles";
import { collapseSection, expandSection } from "../../../utils/collaple-expand-utils";
import { useRef, useState, type JSX } from "react";
import { ChevronRotatePositioner } from "../../ReusableStyledComponents/ChevronRotatePositioner";
import { getImportContentCopyLabel } from "./AddContent.utils";
import { ContentCollectionsPanel } from "../../ContentCollectionsPanel";
import { V3BodyGrey35 } from "../../ReusableStyledComponents";
import { InlineLink } from "../../ReusableStyledComponents/InlineLink";
import { APPLICATION_ROUTES } from "../../../shared-logic/routes";

const IconInfoElem = ({ text, icon }: { text: string | JSX.Element; icon: JSX.Element }) => (
    <ContentResultIconWrapper>
        <div>{icon}</div>
        <div>
            <V3BodyGrey35>{text}</V3BodyGrey35>
        </div>
    </ContentResultIconWrapper>
);

interface AddContentPreviewResultProps {
    item: ContentResponse;
    toggle?: boolean;
    isPlugin?: boolean;
    collections: CollectionResponse[];
    addOrRemoveItemManuallyFromCollection: (add: boolean, articleSlug: string, collectionSlug: string) => Promise<void>;
}

export const AddContentPreviewResult = ({
    item,
    toggle = true,
    isPlugin = false,
    collections,
    addOrRemoveItemManuallyFromCollection
}: AddContentPreviewResultProps) => {
    const contentEl = useRef<HTMLDivElement | null>(null);
    const [panelOpen, setPanelOpen] = useState(false);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [isPartOfCollectionInitially, _] = useState(!!item.collections.length);

    const handleOpen = () => {
        if (!contentEl.current) return;

        if (!panelOpen) {
            expandSection(setPanelOpen, setIsTransitioning, contentEl.current);
        } else {
            collapseSection(setPanelOpen, setIsTransitioning, contentEl.current);
        }
    };

    return (
        <VerticalSpacing bottom={34}>
            <V3ItemPreview item={item} target="_blank" skipUrl={isPlugin} />
            {toggle && (
                <AddItemPreviewResToggle onClick={handleOpen}>
                    <div>
                        <V3CaptionGrey35>Details</V3CaptionGrey35>
                    </div>
                    <div>
                        <ChevronRotatePositioner $panelOpen={panelOpen || (!panelOpen && isTransitioning)}>
                            <V3IconChevronDown />
                        </ChevronRotatePositioner>
                    </div>
                </AddItemPreviewResToggle>
            )}
            <AddContentPreviewResultWrapper ref={contentEl} $noCursor={panelOpen || isPlugin || !toggle} $enabled={toggle}>
                <VerticalSpacing top={12}>
                    {isPartOfCollectionInitially ? (
                        <V3CaptionGrey35>The {getImportContentCopyLabel(item?.type)} is now in the following collections:</V3CaptionGrey35>
                    ) : (
                        <>
                            <V3CaptionGrey35>
                                The {getImportContentCopyLabel(item?.type)} doesn't match the rules for any of your collections, and it's therefore
                                not been added to any collection automatically.
                            </V3CaptionGrey35>
                            <VerticalSpacing top={12}>
                                <V3CaptionGrey35>
                                    As your portfolio can only display content from your collections, this {getImportContentCopyLabel(item?.type)} is
                                    also not on your portfolio currently.
                                </V3CaptionGrey35>
                            </VerticalSpacing>
                        </>
                    )}
                </VerticalSpacing>
                <VerticalSpacing top={12} bottom={18}>
                    <ContentCollectionsPanel
                        isPlugin={isPlugin}
                        contentCollections={item.collections}
                        allCollections={collections}
                        modifyItemOnCollectionCallback={(add, collectionSlug) => {
                            addOrRemoveItemManuallyFromCollection(add, item.canonicalSlug, collectionSlug);
                        }}
                    />
                </VerticalSpacing>
                <VerticalSpacing bottom={18}>
                    <V3CaptionGrey35>
                        <UnstyledInlineLink
                            href="https://authory.com/tutorials/how-to-use-auto-update-settings-for-collections-in-authory"
                            target="_blank"
                        >
                            Learn more about auto-updating collections
                        </UnstyledInlineLink>
                    </V3CaptionGrey35>
                </VerticalSpacing>
                {isPartOfCollectionInitially && (
                    <VerticalSpacing bottom={18}>
                        <IconInfoElem
                            icon={<V3IconSort />}
                            text={
                                "The content item has been added to the collection based on its date, so it may not appear at the top. You can reorder items at any time."
                            }
                        />
                    </VerticalSpacing>
                )}
                <IconInfoElem
                    icon={
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M5 6.5V4C5 3.20435 5.31607 2.44129 5.87868 1.87868C6.44129 1.31607 7.20435 1 8 1C8.79565 1 9.55871 1.31607 10.1213 1.87868C10.6839 2.44129 11 3.20435 11 4V6.5"
                                stroke="#ABB0BA"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                            <rect
                                x="2"
                                y="7"
                                width="12"
                                height="8"
                                rx="1.5"
                                stroke="#ABB0BA"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    }
                    text={
                        <>
                            This item has been added to your <InlineLink href={APPLICATION_ROUTES.CONTENT}>Content Archive</InlineLink>, meaning you
                            can use it in any other collection or share it directly in the future.
                        </>
                    }
                />
            </AddContentPreviewResultWrapper>
        </VerticalSpacing>
    );
};
